import React, { useEffect, useState } from "react";
// 1. Import PKCE challenge helper
import pkceChallenge from "pkce-challenge";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { FaShieldAlt, FaArrowLeft, FaIdCard } from "react-icons/fa"; // Added FaIdCard
import logo from "../images/moh-logo.png";
import faydaLogo from "../images/fayda_logo.png";
import apiRequest from "../services/api";

const Login = ({ onClose, onLogin }) => {
  const [activeTab, setActiveTab] = useState("login");
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [rawInstructions, setRawInstructions] = useState(
    "Loading instructions..."
  );

  const [loginForm, setLoginForm] = useState({ username: "", password: "" });
  const [forgotEmail, setForgotEmail] = useState("");
  const [fan, setFan] = useState("");
  const [idno, setIdno] = useState("");
  const [resetForm, setResetForm] = useState({
    username: "",
    token: "",
    newPassword: "",
    confirmPassword: "",
  });

  const location = useLocation();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  // --- E-SIGNET (FAYDA) INTEGRATION START ---
  const handleFaydaLogin = async () => {
    try {
      setIsSubmitting(true);
      setError("");

      // 1. Generate PKCE Verifier and Challenge
      const challenge = await pkceChallenge();

      // 2. Store verifier in SessionStorage (we need this after redirect)
      sessionStorage.setItem("fayda_verifier", challenge.code_verifier);

      // 3. Generate a random state for security
      const state = Math.random().toString(36).substring(7);
      sessionStorage.setItem("fayda_state", state);

      // 4. Define the specific Claims requested from Fayda
      const claims = {
        userinfo: {
          name: { essential: true },
          phone_number: { essential: true },
          email: { essential: true },
          picture: { essential: true },
          gender: { essential: true },
          birthdate: { essential: true },
          address: { essential: true },
          nationality: { essential: true },
          individual_id: { essential: true },
        },
        id_token: {},
      };

      // 5. Construct Authorization URL
      const clientId = "CFsdz0pGBpHLYkCCPwGMDe4xMSyH4jjaY-mrxtnwvUU";
      const redirectUri = window.location.origin + "/auth/esignet/callback";

      const params = new URLSearchParams({
        client_id: clientId,
        response_type: "code",
        redirect_uri: redirectUri,
        scope: "openid profile email",
        state: state,
        code_challenge: challenge.code_challenge,
        code_challenge_method: "S256",
        acr_values: "mosip:idp:acr:generated-code",
        claims_locales: "en",
        claims: JSON.stringify(claims),
      });

      // 6. Redirect to Fayda
      // URLSearchParams.toString() automatically handles the URL encoding (urllib.parse.quote equivalent)
      window.location.href = `https://auth.fayda.et/authorize?${params.toString()}`;
    } catch (err) {
      setError("Failed to initiate Fayda login.");
      setIsSubmitting(false);
    }
  };
  // --- E-SIGNET INTEGRATION END ---

  const handleSafeClose = () => {
    if (token && onClose) {
      onClose();
    } else {
      setError("Please sign in to access the application.");
    }
  };

  useEffect(() => {
    const fetchInstructions = async () => {
      try {
        const json = await apiRequest(`users/note`, {});
        if (json.success) {
          setRawInstructions(json.data);
        }
      } catch (err) {
        setRawInstructions(
          "Secure Authentication Required.\nPlease enter your credentials. "
        );
      }
    };
    fetchInstructions();
  }, []);

  const handleLoginChange = (e) => {
    const { name, value } = e.target;
    setLoginForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!loginForm.username || !loginForm.password)
      return setError("Username and password required.");
    try {
      setIsSubmitting(true);
      const response = await apiRequest("auth/login", {
        method: "POST",
        body: loginForm,
      });
      const key = Number(response.akey ?? 0);

      // Force password recovery
      if (key !== 0) {
        setActiveTab("forgot");
        return;
      }

      localStorage.setItem("token", response.token);
      localStorage.setItem("user", JSON.stringify(response));
      console.log("login data: ", response);
      if (response.status === "NEW") {
        logout();
        return;
      }

      if (onLogin) onLogin(response);
      if (onClose) onClose();
      navigate(location.pathname + location.search);
    } catch {
      setError("Login failed. Check credentials.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLinkstartSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccessMsg("");
    if (!fan || !idno) {
      return setError("Please enter your ID NO and Fayda FAN.");
    }

    try {
      setIsSubmitting(true);
      const response = await apiRequest(`auth/fayda/link-start`, {
        method: "POST",
        body: {
          username: idno, // Maps to FaydaLinkDto.username
          fan: fan, // Maps to FaydaLinkDto.fan
        },
      });

      localStorage.setItem("cacheKey", response.cacheKey);
      if (response.fan) localStorage.setItem("fan", response.fan);

      setSuccessMsg("Link task initialized successfully!");
      if (typeof handleFaydaLogin === "function") {
        handleFaydaLogin();
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccessMsg("");
    if (!forgotEmail) return setError("Please enter your username/mobile.");
    try {
      setIsSubmitting(true);
      await apiRequest(
        `auth/forgotPassword?username=${encodeURIComponent(forgotEmail)}`,
        {
          method: "PUT",
        }
      );
      setResetForm((prev) => ({ ...prev, username: forgotEmail }));
      setSuccessMsg("OTP sent to your mobile.");
      setActiveTab("reset");
    } catch (err) {
      setError(err.message || "Unable to send OTP.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResetSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (resetForm.newPassword !== resetForm.confirmPassword)
      return setError("Passwords do not match.");
    try {
      setIsSubmitting(true);
      await apiRequest(
        `auth/resetPassword?username=${encodeURIComponent(
          resetForm.username
        )}&token=${encodeURIComponent(
          resetForm.token
        )}&newPassword=${encodeURIComponent(resetForm.newPassword)}`,
        { method: "PUT" }
      );
      setSuccessMsg("Password reset successful! Please login.");
      setTimeout(() => setActiveTab("login"), 2000);
    } catch {
      setError("Unable to reset password.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const logout = async () => {
    try {
      const token = localStorage.getItem("token");
      if (token) await apiRequest("auth/logout", { method: "POST" });
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      setActiveTab("startFayda");
    } catch (error) {
      console.error("Logout failed", error);
      localStorage.clear();
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/70 z-50 flex items-center justify-center p-2 md:p-4 backdrop-blur-md">
      <div className="relative bg-white rounded-3xl w-full max-w-4xl flex flex-col md:flex-row h-auto max-h-[95vh] md:max-h-[600px] overflow-hidden shadow-2xl border border-sky-100">
        {token && (
          <button
            onClick={handleSafeClose}
            className="absolute top-4 right-4 text-slate-400 hover:text-sky-600 z-20 p-2 bg-white/80 rounded-full md:bg-transparent"
          >
            ✕
          </button>
        )}

        {/* Left Side: Information/Announcement */}
        <div className="w-full md:w-5/12 bg-sky-50 p-6 md:p-8 flex flex-col border-b md:border-b-0 md:border-r border-sky-100 overflow-y-auto no-scrollbar md:overflow-hidden">
          <div className="mb-4 flex-shrink-0">
            <div className="flex items-center gap-3 mb-4">
              <img
                src={logo}
                alt="Admin Logo"
                className="h-14 md:h-18 object-contain"
              />
              <div>
                <h1 className="text-xl md:text-2xl font-black text-slate-800 tracking-tighter">
                  EHPLE
                </h1>
                <p className="text-[10px] md:text-[11px] font-normal text-slate-400 -mt-1.5 ml-0.5 tracking-[0.1em] uppercase opacity-80">
                  Ethiopian Health Professionals Licensing Examination
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 ml-1">
              <div className="h-1 w-8 bg-sky-500 rounded-full"></div>
              <h2 className="text-[10px] md:text-xs font-bold text-sky-700 uppercase tracking-widest">
                Announcement
              </h2>
            </div>
          </div>

          <div className="flex-grow">
            <textarea
              readOnly
              value={rawInstructions || ""}
              className="w-full border-none bg-transparent rounded px-0 py-2 text-sm text-slate-600 focus:ring-0 resize-none min-h-[80px] md:min-h-0"
              rows={window.innerWidth < 768 ? 6 : 15}
            />
          </div>
        </div>

        {/* Right Side: Forms */}
        <div className="w-full md:w-7/12 p-6 md:p-12 overflow-y-auto no-scrollbar bg-white flex flex-col justify-center">
          {error && (
            <div className="mb-4 p-3 bg-red-50 text-red-600 text-xs font-bold rounded-xl border border-red-100">
              {error}
            </div>
          )}
          {successMsg && (
            <div className="mb-4 p-3 bg-sky-50 text-sky-700 text-xs font-bold rounded-xl border border-sky-100">
              {successMsg}
            </div>
          )}

          {activeTab === "login" && (
            <div className="animate-in slide-in-from-right duration-300">
              <h2 className="text-xl md:text-2xl font-extrabold text-slate-800 mb-6">
                Welcome Back
              </h2>
              <form
                onSubmit={handleLoginSubmit}
                className="space-y-4 md:space-y-5"
              >
                <div>
                  <label className="text-[10px] md:text-xs font-bold text-sky-700 uppercase ml-1">
                    FAYDA FAN
                  </label>
                  <input
                    name="username"
                    value={loginForm.username}
                    onChange={handleLoginChange}
                    className="w-full mt-1 bg-slate-50 border-2 border-slate-100 px-4 py-2.5 md:py-3 rounded-xl focus:border-sky-400 focus:bg-white transition-all outline-none text-slate-700 font-medium text-sm md:text-base"
                    placeholder="Enter FAN"
                  />
                </div>
                <div>
                  <label className="text-[10px] md:text-xs font-bold text-sky-700 uppercase ml-1">
                    Password
                  </label>
                  <input
                    type="password"
                    name="password"
                    value={loginForm.password}
                    onChange={handleLoginChange}
                    className="w-full mt-1 bg-slate-50 border-2 border-slate-100 px-4 py-2.5 md:py-3 rounded-xl focus:border-sky-400 focus:bg-white transition-all outline-none text-slate-700 font-medium text-sm md:text-base"
                    placeholder="••••••••"
                  />
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-sky-500 hover:bg-sky-600 text-white font-bold py-3.5 md:py-4 rounded-xl shadow-lg transition-all active:scale-95"
                >
                  {isSubmitting ? "Processing..." : "Continue"}
                </button>
              </form>

              {/* --- NEW FAYDA LOGIN BUTTON --- */}
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-slate-200"></div>
                </div>
                <div className="relative flex justify-center text-xs uppercase"></div>
              </div>

              <button
                type="button"
                onClick={() => setActiveTab("startFayda")}
                disabled={isSubmitting}
                className="w-full bg-white border-0 border-sky-500 text-sky-600 hover:bg-sky-50 font-bold py-3 md:py-3.5 rounded-xl transition-all active:scale-95 flex items-center justify-center gap-3"
              >
                <img
                  src={faydaLogo}
                  alt="Fayda Logo"
                  className="h-6 w-auto object-contain"
                />
                Link Fayda (FAN)
              </button>

              <button
                onClick={() => setActiveTab("forgot")}
                className="mt-6 text-sky-600 text-sm font-bold block w-full text-center hover:underline"
              >
                Forgot Password?
              </button>
            </div>
          )}

          {/* ... Other tabs (forgot/reset) remain the same ... */}
          {activeTab === "startFayda" && (
            <div className="animate-in slide-in-from-right duration-300">
              <button
                onClick={() => setActiveTab("login")}
                className="flex items-center gap-2 text-slate-400 hover:text-sky-600 mb-4 text-xs md:text-sm font-bold"
              >
                <FaArrowLeft /> Back
              </button>
              <h2 className="text-xl md:text-2xl font-extrabold text-slate-800 mb-2">
                Link Fayda Number
              </h2>
              <form onSubmit={handleLinkstartSubmit} className="space-y-4">
                <input
                  value={idno}
                  onChange={(e) => setIdno(e.target.value)}
                  className="w-full bg-slate-50 border-2 border-slate-100 px-4 py-3 rounded-xl focus:border-sky-400 outline-none font-medium text-slate-700 text-sm md:text-base"
                  placeholder="EHPLE IDNO"
                />
                <input
                  value={fan}
                  onChange={(e) => setFan(e.target.value)}
                  className="w-full bg-slate-50 border-2 border-slate-100 px-4 py-3 rounded-xl focus:border-sky-400 outline-none font-medium text-slate-700 text-sm md:text-base"
                  placeholder="Fayda FAN"
                />
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-sky-500 text-white font-bold py-3.5 md:py-4 rounded-xl shadow-lg"
                >
                  {isSubmitting ? "Linking..." : "Continue"}
                </button>
              </form>
            </div>
          )}

          {/* ... Other tabs (forgot/reset) remain the same ... */}
          {activeTab === "forgot" && (
            <div className="animate-in slide-in-from-right duration-300">
              <button
                onClick={() => setActiveTab("login")}
                className="flex items-center gap-2 text-slate-400 hover:text-sky-600 mb-4 text-xs md:text-sm font-bold"
              >
                <FaArrowLeft /> Back
              </button>
              <h2 className="text-xl md:text-2xl font-extrabold text-slate-800 mb-2">
                Recovery
              </h2>
              <form onSubmit={handleForgotSubmit} className="space-y-4">
                <input
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  className="w-full bg-slate-50 border-2 border-slate-100 px-4 py-3 rounded-xl focus:border-sky-400 outline-none font-medium text-slate-700 text-sm md:text-base"
                  placeholder="Username / Fayda No."
                />
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-sky-500 text-white font-bold py-3.5 md:py-4 rounded-xl shadow-lg"
                >
                  {isSubmitting ? "Sending..." : "Send OTP"}
                </button>
              </form>
            </div>
          )}

          {activeTab === "reset" && (
            <div className="animate-in slide-in-from-right duration-300">
              <button
                onClick={() => setActiveTab("login")}
                className="flex items-center gap-2 text-slate-400 hover:text-sky-600 mb-4 text-xs md:text-sm font-bold"
              >
                <FaArrowLeft /> Back
              </button>
              <h2 className="text-xl md:text-2xl font-extrabold text-slate-800 mb-6">
                Reset Password
              </h2>
              <form
                onSubmit={handleResetSubmit}
                className="space-y-3 md:space-y-4"
              >
                <input
                  placeholder="Enter OTP"
                  value={resetForm.token}
                  onChange={(e) =>
                    setResetForm({ ...resetForm, token: e.target.value })
                  }
                  className="w-full bg-slate-50 border-2 border-slate-100 px-4 py-3 rounded-xl focus:border-sky-400 outline-none font-medium text-slate-700 text-sm md:text-base"
                />
                <input
                  type="password"
                  placeholder="New Password"
                  value={resetForm.newPassword}
                  onChange={(e) =>
                    setResetForm({ ...resetForm, newPassword: e.target.value })
                  }
                  className="w-full bg-slate-50 border-2 border-slate-100 px-4 py-3 rounded-xl focus:border-sky-400 outline-none font-medium text-slate-700 text-sm md:text-base"
                />
                <input
                  type="password"
                  placeholder="Confirm New Password"
                  value={resetForm.confirmPassword}
                  onChange={(e) =>
                    setResetForm({
                      ...resetForm,
                      confirmPassword: e.target.value,
                    })
                  }
                  className="w-full bg-slate-50 border-2 border-slate-100 px-4 py-3 rounded-xl focus:border-sky-400 outline-none font-medium text-slate-700 text-sm md:text-base"
                />
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-sky-500 text-white font-bold py-3.5 md:py-4 rounded-xl shadow-lg"
                >
                  {isSubmitting ? "Updating..." : "Update Password"}
                </button>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;
